/**
 * Kundenliste (Einrichten-Neubau P5).
 *
 * Ersetzt die alte Kunden-Seite mit ihren vier konkurrierenden Filterquellen
 * und dem Badge, das heimlich filterte: EIN Suchfeld, EIN Gruppen-Filter,
 * Inaktive sichtbar. Der ERP-Import (Lieferadressen) läuft mit Vorschau und
 * sichtbarem Ergebnis. Gruppen-Zugehörigkeit wird am Kunden oder an der
 * Gruppe gepflegt — nicht mehr über Bulk-Modals mit Nebentüren.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import {
  Karte,
  Kennung,
  Kopfzelle,
  Notiz,
  Tabelle,
  Zelle,
  Zustand,
} from '../../components/leitstand';
import { CsvDropzone, ImportField, FieldMapping, ImportResult } from '../../components/CsvDropzone';

export interface Kunde {
  id: string;
  name: string;
  shortName?: string | null;
  externalCustomerNo?: string | null;
  externalDeliveryId?: string | null;
  customerGroupId?: string | null;
  isActive: boolean;
  updatedAt: string;
}

interface Gruppe {
  id: string;
  name: string;
}

const IMPORT_FELDER: ImportField[] = [
  {
    key: 'externalDeliveryId',
    label: 'Lieferadr.-Nr.',
    required: true,
    description: 'Eindeutige Lieferadress-Nummer aus dem ERP',
  },
  {
    key: 'externalCustomerNo',
    label: 'Kundennr.',
    required: true,
    description: 'ERP-Kundennummer',
  },
  { key: 'name', label: 'Name', required: true },
];

export default function KundenListe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [gruppen, setGruppen] = useState<Gruppe[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [suchtext, setSuchtext] = useState('');
  const [gruppe, setGruppe] = useState(params.get('gruppe') ?? '');
  const [importOffen, setImportOffen] = useState(false);

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const [kRes, gRes] = await Promise.all([
        fetch('/api/einrichten/objekt/kunden', { credentials: 'include' }),
        fetch('/api/einrichten/objekt/kundengruppen', { credentials: 'include' }),
      ]);
      if (!kRes.ok) throw new Error('Die Kundenliste konnte nicht geladen werden');
      setKunden(((await kRes.json()).objekte ?? []) as Kunde[]);
      if (gRes.ok) setGruppen(((await gRes.json()).objekte ?? []) as Gruppe[]);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Kundenliste konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void lade();
  }, [lade]);

  const importiere = async (
    data: Record<string, unknown>[],
    mapping: FieldMapping,
    options?: { dryRun?: boolean; applyUpdates?: boolean },
  ): Promise<ImportResult> => {
    const res = await fetch('/api/admin/customers/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ data, mapping, options }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.error || 'Import fehlgeschlagen');
    }
    const result = await res.json();
    if (!options?.dryRun) void lade();
    return result;
  };

  const gruppenName = (id?: string | null) => gruppen.find((g) => g.id === id)?.name;

  const begriff = suchtext.trim().toLowerCase();
  const gefiltert = kunden.filter((k) => {
    if (gruppe && k.customerGroupId !== gruppe) return false;
    if (!begriff) return true;
    return (
      k.name.toLowerCase().includes(begriff) ||
      k.shortName?.toLowerCase().includes(begriff) ||
      k.externalCustomerNo?.toLowerCase().includes(begriff) ||
      k.externalDeliveryId?.toLowerCase().includes(begriff)
    );
  });

  return (
    <LeitstandRahmen
      titel="Kunden"
      benutzer={user?.displayName}
      werkzeuge={
        <span className="flex gap-2">
          <button
            onClick={() => setImportOffen((o) => !o)}
            className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
          >
            ERP-Import
          </button>
          <button
            onClick={() => navigate('/einrichten/kunden/neu')}
            className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz"
          >
            Kunde anlegen
          </button>
        </span>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/einrichten" className="text-[13px] text-tinte-3 hover:text-tinte-2">
            ← Katalog
          </Link>
          <input
            type="text"
            value={suchtext}
            onChange={(e) => setSuchtext(e.target.value)}
            placeholder="Name, Kundennr., Lieferadr.-Nr. …"
            className="min-w-[220px] flex-1 rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 text-sm text-tinte placeholder:text-tinte-3 focus:border-akzent focus:outline-none sm:max-w-xs"
          />
          <select
            value={gruppe}
            onChange={(e) => setGruppe(e.target.value)}
            className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 text-sm text-tinte"
          >
            <option value="">Alle Gruppen</option>
            {gruppen.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>

        {importOffen && (
          <Karte
            titel="ERP-Import"
            marke="Lieferadressen — mit Vorschau, übernommen wird erst auf Knopf"
          >
            <CsvDropzone
              fields={IMPORT_FELDER}
              onImport={importiere}
              onClose={() => setImportOffen(false)}
              title="Kunden importieren"
              description="Export der Lieferadressen aus dem ERP. Die Lieferadr.-Nr. ist der Schlüssel — bestehende Kunden werden aktualisiert, neue angelegt."
            />
          </Karte>
        )}

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && kunden.length === 0 && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Kunden …</p>
        )}

        {!laden && !fehler && (
          <Karte titel="Kunden" marke={`${gefiltert.length} von ${kunden.length}`} tabelle>
            {gefiltert.length === 0 ? (
              <p className="py-6 text-center text-sm text-tinte-3">Kein Treffer im Filter.</p>
            ) : (
              <div className="overflow-x-auto">
                <Tabelle>
                  <thead>
                    <tr>
                      <Kopfzelle>Name</Kopfzelle>
                      <Kopfzelle>Kundennr.</Kopfzelle>
                      <Kopfzelle>Gruppe</Kopfzelle>
                      <Kopfzelle>Zustand</Kopfzelle>
                      <Kopfzelle rechts>Geändert</Kopfzelle>
                    </tr>
                  </thead>
                  <tbody>
                    {gefiltert.slice(0, 200).map((k, i, arr) => {
                      const letzte = i === arr.length - 1;
                      return (
                        <tr
                          key={k.id}
                          onClick={() => navigate(`/einrichten/kunden/${k.id}`)}
                          className={`cursor-pointer hover:bg-flaeche-2 ${k.isActive ? '' : 'opacity-60'}`}
                        >
                          <Zelle letzte={letzte}>{k.name}</Zelle>
                          <Zelle zahl letzte={letzte}>
                            {k.externalCustomerNo ?? '—'}
                          </Zelle>
                          <Zelle letzte={letzte}>
                            {gruppenName(k.customerGroupId) ? (
                              <Kennung>{gruppenName(k.customerGroupId)}</Kennung>
                            ) : (
                              <span className="text-tinte-3">—</span>
                            )}
                          </Zelle>
                          <Zelle letzte={letzte}>
                            {k.isActive ? (
                              <Zustand art="gut">aktiv</Zustand>
                            ) : (
                              <span className="font-zahl text-[11px] text-tinte-3">
                                außer Betrieb
                              </span>
                            )}
                          </Zelle>
                          <Zelle rechts zahl letzte={letzte}>
                            {new Date(k.updatedAt).toLocaleDateString('de-DE', {
                              timeZone: 'Europe/Berlin',
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                            })}
                          </Zelle>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tabelle>
                {gefiltert.length > 200 && (
                  <p className="pt-3 text-center text-xs text-tinte-3">
                    {gefiltert.length - 200} weitere — Suche eingrenzen, um sie zu sehen.
                  </p>
                )}
              </div>
            )}
          </Karte>
        )}
      </div>
    </LeitstandRahmen>
  );
}
